'use client'

import * as Sentry from '@sentry/nextjs'
import { Copy, Key, Trash2 } from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'

import { AuthModal } from '@/components/ui/auth-modal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/hooks/use-toast'
import { fetchWithAuth, handleApiError } from '@/utils/api-error-handler'
import { markOnboardingTaskComplete } from '@/utils/onboarding'

type ApiKey = {
  id: string
  name: string
  prefix: string
  created_at: string
  expires_at: string | null
  revoked_at: string | null
  last_used_at: string | null
}

type ApiKeysResponse = {
  data: ApiKey[]
  count: number
}

type ApiKeyFilter = 'user' | 'agent'

const API_URL = process.env.NEXT_PUBLIC_API_URL
const AGENT_KEY_PREFIX = 'agents-v2-'

function formatDate(value: string | null | undefined) {
  if (!value) return '-'
  try {
    const d = new Date(value)
    if (isNaN(d.getTime())) return '-'
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return '-'
  }
}

function getStatus(key: ApiKey): 'active' | 'revoked' | 'expired' {
  if (key.revoked_at) return 'revoked'
  if (key.expires_at) {
    const now = Date.now()
    const exp = new Date(key.expires_at).getTime()
    if (!isNaN(exp) && exp < now) return 'expired'
  }
  return 'active'
}

function isAgentGeneratedKey(key: ApiKey): boolean {
  return String(key.name || '').trim().toLowerCase().startsWith(AGENT_KEY_PREFIX)
}

export default function ApiKeysPage() {
  const searchParams = useSearchParams()
  const teamIdParam = searchParams.get('team_id')
  const [isLoading, setIsLoading] = useState(true)
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [deleteTarget, setDeleteTarget] = useState<ApiKey | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [createdSecret, setCreatedSecret] = useState<{ name: string; api_key: string } | null>(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [activeFilter, setActiveFilter] = useState<ApiKeyFilter>('user')

  const loadKeys = async () => {
    if (!API_URL) return
    setIsLoading(true)
    try {
      const response = await fetchWithAuth(`${API_URL}/v1/api-keys`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include'
      }, undefined, { skipUnauthorizedRedirect: true })
      if (response.status === 401) {
        setKeys([])
        setShowAuthModal(true)
        return
      }
      if (!response.ok) {
        throw new Error(`Failed to load API keys (${response.status})`)
      }
      const json = (await response.json()) as ApiKeysResponse
      setKeys(Array.isArray(json.data) ? json.data : [])
    } catch (error) {
      // Avoid auto-redirect to /login. Keep page visible.
      try {
        await handleApiError(error)
      } catch {
        // ignore
      }
      toast({ title: 'Failed to load API keys', variant: 'destructive' })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadKeys()
  }, [teamIdParam])

  const onConfirmDelete = async () => {
    if (!deleteTarget || !API_URL) return
    setIsDeleting(true)
    try {
      const response = await fetchWithAuth(`${API_URL}/v1/api-keys/${deleteTarget.id}`, {
        method: 'DELETE',
        credentials: 'include'
      }, undefined, { skipUnauthorizedRedirect: true })
      if (response.status === 401) {
        setShowAuthModal(true)
        return
      }
      if (!response.ok) {
        throw new Error(`Failed to revoke key (${response.status})`)
      }
      // Remove from list after successful revoke
      setKeys(prev => prev.filter(k => k.id !== deleteTarget.id))
      toast({ title: 'API key revoked' })
      setDeleteTarget(null)
    } catch (error) {
      try {
        await handleApiError(error)
      } catch {
        // ignore
      }
      toast({ title: 'Failed to revoke key', variant: 'destructive' })
    } finally {
      setIsDeleting(false)
    }
  }

  const rows = useMemo(() => {
    return keys.map(k => ({
      ...k,
      status: getStatus(k),
      isAgentGenerated: isAgentGeneratedKey(k)
    }))
  }, [keys])

  const userRows = useMemo(() => rows.filter(row => !row.isAgentGenerated), [rows])
  const agentRows = useMemo(() => rows.filter(row => row.isAgentGenerated), [rows])
  const filteredRows = activeFilter === 'agent' ? agentRows : userRows

  const onCreateApiKey = async () => {
    if (!API_URL || !newKeyName.trim()) return
    setIsCreating(true)
    try {
      const response = await fetchWithAuth(`${API_URL}/v1/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ name: newKeyName.trim() })
      }, undefined, { skipUnauthorizedRedirect: true })
      if (response.status === 401) {
        setShowAuthModal(true)
        return
      }
      if (!response.ok) {
        throw new Error(`Failed to create API key (${response.status})`)
      }
      const json = await response.json() as {
        id: string
        name: string
        prefix: string
        created_at: string
        expires_at: string | null
        api_key: string
      }
      // Update list
      setKeys(prev => [
        {
          id: json.id,
          name: json.name,
          prefix: json.prefix,
          created_at: json.created_at,
          expires_at: json.expires_at ?? null,
          revoked_at: null,
          last_used_at: null
        },
        ...prev
      ])
      // Show secret modal
      setCreatedSecret({ name: json.name, api_key: json.api_key })
      try {
        if (typeof Sentry.metrics?.count === 'function') {
          Sentry.metrics.count('api_key_create', 1)
          void Sentry.flush(2000)
        }
        void markOnboardingTaskComplete('api_key_create')
      } catch (metricErr) {
        console.error('[Sentry] Failed to send api_key_create metric', metricErr)
      }
      setIsCreateOpen(false)
      setNewKeyName('')
    } catch (error) {
      try {
        await handleApiError(error)
      } catch {
        // ignore
      }
      toast({ title: 'Failed to create API key', variant: 'destructive' })
    } finally {
      setIsCreating(false)
    }
  }

  const copySecret = async () => {
    if (!createdSecret?.api_key) return
    try {
      await navigator.clipboard.writeText(createdSecret.api_key)
      toast({ title: 'Secret key copied' })
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' })
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="pl-4 pr-4 pt-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">API Keys</h1>
            <p className="text-sm text-muted-foreground">Manage your API keys. Get started by reading the <a href="https://docs.fastfold.ai/quick-start" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Quick Start Guide</a> guide.
                      </p>
            <div className="pt-2">
              <Tabs value={activeFilter} onValueChange={(value) => setActiveFilter(value as ApiKeyFilter)} className="w-full">
                <TabsList className="h-8 justify-start bg-muted/50 p-1">
                  <TabsTrigger value="user" className="text-xs">
                    User generated ({userRows.length})
                  </TabsTrigger>
                  <TabsTrigger value="agent" className="text-xs">
                    Agent generated ({agentRows.length})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              {activeFilter === 'agent' ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Agent-generated keys are auto-created for sandbox sessions.
                </p>
              ) : null}
            </div>
          </div>
          <div>
            <Button onClick={() => setIsCreateOpen(true)}>
               <Key className="h-4 w-4 mr-2" /> Create API key
            </Button>
          </div>
        </div>
      </div>

      <div className="pl-4 pr-4 pb-4">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[28%]">Name</TableHead>
                <TableHead className="w-[14%]">Status</TableHead>
                <TableHead className="w-[22%]">Secret key</TableHead>
                <TableHead className="w-[18%]">Created</TableHead>
                <TableHead className="w-[18%]">Last used</TableHead>
                <TableHead className="w-[70px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {Array.from({ length: 6 }).map((_, cellIndex) => (
                        <TableCell key={`skeleton-cell-${cellIndex}`} className="py-4">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    {activeFilter === 'agent' ? 'No agent-generated API keys found.' : 'No user-generated API keys found.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map(key => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      {key.status === 'active' && <Badge>Active</Badge>}
                      {key.status === 'revoked' && <Badge variant="destructive">Revoked</Badge>}
                      {key.status === 'expired' && <Badge variant="secondary">Expired</Badge>}
                    </TableCell>
                    <TableCell className="font-mono">{key.prefix}</TableCell>
                    <TableCell>{formatDate(key.created_at)}</TableCell>
                    <TableCell>{formatDate(key.last_used_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(key)}
                        aria-label={`Revoke ${key.name}`}
                      >
                        <Trash2 className="h-5 w-5 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create API Key Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={open => { setIsCreateOpen(open); if (!open) { setNewKeyName('') } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>
              Enter a name to help you identify this key.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              placeholder="Key name (e.g., CI/CD, local dev)"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={onCreateApiKey} disabled={isCreating || !newKeyName.trim()}>
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API key</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will revoke access for the API key{' '}
              <span className="font-medium">{deleteTarget?.name}</span>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onConfirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Revoking...' : 'Revoke'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secret Key Modal */}
      <Dialog open={!!createdSecret} onOpenChange={open => { if (!open) setCreatedSecret(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save your key</DialogTitle>
            <DialogDescription>
              Please save your secret key in a safe place since you won't be able to view it again.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex items-center gap-2">
            <Input readOnly value={createdSecret?.api_key ?? ''} className="font-mono" />
            <Button variant="secondary" onClick={copySecret}>
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedSecret(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </div>
  )
}


